/**
 * Buy now — checkout for one product, without losing the bag
 * ------------------------------------------------------------------
 * Every "Buy now" control used to add its product to the cart and then send
 * the shopper to checkout. Checkout, by definition, charges the whole cart, so
 * a shopper with three things in their bag who pressed "Buy now" on a fourth
 * was shown — and would have paid for — all four.
 *
 * Shopify gives a theme no way to check out a subset of the cart: /checkout
 * takes the cart, and a cart permalink (/cart/<variant>:<qty>) replaces the
 * cart before it goes to checkout. Both were measured against the dev store.
 * So the cart really is the only vehicle, and the fix is to lend it to the
 * Buy-now item and then give it back:
 *
 *   1. snapshot the cart's lines into localStorage,
 *   2. clear the cart and add only the Buy-now lines,
 *   3. hand off to the theme's own pre-checkout screen, which reads the cart
 *      and therefore now shows exactly one product with no changes of its own,
 *   4. restore the snapshot the moment the shopper is back on the storefront —
 *      whether they paid, abandoned at checkout, or wandered off the
 *      pre-checkout screen.
 *
 * Step 4 is what keeps the promise: the bag they had before pressing Buy now
 * is the bag they have afterwards. The snapshot outlives the tab, so a shopper
 * who closes the window mid-checkout still gets their bag back on the next
 * visit.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'pf:buy-now-cart-restore';

  function routes() {
    return window.PFRoutes || {};
  }

  function cartUrl(suffix) {
    var base = routes().cart || '/cart';
    return base.replace(/\/$/, '') + suffix;
  }

  /* localStorage throws outright in some privacy modes, so every touch is
     guarded. A browser that cannot remember the bag still gets Buy now when
     the bag is empty; with items in it, start() refuses rather than lose them. */
  function readSnapshot() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && Array.isArray(parsed.items) ? parsed : null;
    } catch (error) {
      return null;
    }
  }

  function writeSnapshot(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function clearSnapshot() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* nothing to clear */
    }
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body || {})
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart request failed.');
      return response.json().catch(function () { return {}; });
    });
  }

  function getCart() {
    return fetch(cartUrl('.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Unable to read the cart.');
      return response.json();
    });
  }

  /* Everything /cart/add.js can put back. Properties and selling plans are
     carried so a restored line is the same line, not just the same variant. */
  function snapshotLines(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.map(function (item) {
      var line = { id: item.id, quantity: item.quantity };
      if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
      if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) {
        line.selling_plan = item.selling_plan_allocation.selling_plan.id;
      }
      return line;
    });
  }

  /* The pre-checkout screens are the one place the cart is *meant* to hold
     only the Buy-now item, so the restore must not fire there. */
  function isPreCheckoutPage() {
    var here = window.location.pathname.replace(/\/$/, '');
    var candidates = [routes().checkout, routes().checkoutNewUser];
    return candidates.some(function (url) {
      if (!url) return false;
      var path = String(url).split('?')[0].replace(/\/$/, '');
      return path !== '' && path === here;
    });
  }

  function announce() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'pf-buy-now' } }));
  }

  /**
   * Hand the cart over to `lines` and go to `destination`.
   * Returns a promise that only resolves if the hand-off failed — on success
   * the browser is already navigating.
   */
  function start(lines, destination) {
    if (!Array.isArray(lines) || !lines.length) return Promise.reject(new Error('Nothing to buy.'));
    var target = destination || routes().checkout || '/checkout';
    var cleared = false;

    /* The bag is only replaced once it is known and remembered. A failed cart
       read used to snapshot as "empty" and then clear the real bag, and a
       browser that could not store the snapshot lost a non-empty bag outright
       (docs/backend-functionality-audit-2026-09-10.md). Either case now stops
       before anything is cleared. */
    return getCart()
      .then(function (cart) {
        /* An empty snapshot is still a snapshot: a shopper whose bag was
           empty when they pressed Buy now should find it empty again if they
           do not go through with it, not holding an item they never chose to
           keep. Restoring to "exactly what was there" has to include nothing. */
        var items = snapshotLines(cart);
        var saved = writeSnapshot({ items: items, savedAt: Date.now() });
        if (!saved && items.length) {
          throw new Error('We could not save your bag, so Buy now was stopped. Please use Add to cart.');
        }
        cleared = true;
        return postJSON(cartUrl('/clear.js'));
      })
      .then(function () {
        return postJSON(cartUrl('/add.js'), { items: lines });
      })
      .then(function () {
        announce();
        window.location.assign(target);
      })
      .catch(function (error) {
        /* The bag was already cleared if we got that far, so put it back
           before surfacing the failure — a failed Buy now must not cost the
           shopper their cart. Before the clear, the bag is untouched and an
           older snapshot must not be replayed over it. */
        if (!cleared) throw error;
        return restore().then(function () { throw error; });
      });
  }

  var restorePending = null;

  /** Put the snapshot back and forget it. Safe to call when there is none. */
  function restore() {
    if (restorePending) return restorePending;
    var snapshot = readSnapshot();
    if (!snapshot) return Promise.resolve(false);

    /* Clear first: an abandoned checkout leaves the Buy-now item in the cart,
       and the promise made to the shopper is the bag they had *before* they
       pressed Buy now — not that bag plus the thing they did not buy. */
    restorePending = postJSON(cartUrl('/clear.js'))
      .then(function () {
        if (!snapshot.items.length) return {};
        return postJSON(cartUrl('/add.js'), { items: snapshot.items });
      })
      .then(function () {
        clearSnapshot();
        announce();
        return true;
      })
      .catch(function () {
        /* Leave the snapshot in place so the next page load tries again. */
        return false;
      })
      .then(function (result) {
        restorePending = null;
        return result;
      });

    return restorePending;
  }

  function maybeRestore() {
    if (isPreCheckoutPage()) return;
    if (!readSnapshot()) return;
    restore();
  }

  window.PFBuyNow = { start: start, restore: restore, isPreCheckoutPage: isPreCheckoutPage };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeRestore);
  } else {
    maybeRestore();
  }
  /* Coming back from Shopify's checkout is often a bfcache restore, which
     fires no DOMContentLoaded. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) maybeRestore();
  });
})();
